import type { SeriesWithSlideDeck as ConversationalUISeriesWithSlideDeck } from '@mentimeter/conversational-ui-schema/api-types-overrides';
import type { SeriesWithSlideDeck as EditorSeriesWithSlideDeck } from '@mentimeter/editor-schema/api-types-overrides';
import type { SeriesWithSlideDeck as FollowerSeriesWithSlideDeck } from '@mentimeter/follower-schema/api-types-overrides';
import type { SeriesWithSlideDeck as PowerPointSeriesWithSlideDeck } from '@mentimeter/integrations-schema/power-point';
import type { SeriesWithSlideDeck as PresentationSeriesWithSlideDeck } from '@mentimeter/presentation-schema/api-types-overrides';
import type { SeriesWithSlideDeck as ViewOnlySeriesWithSlideDeck } from '@mentimeter/view-only-schema/api-types-overrides';
import { createContext, useMemo, type PropsWithChildren } from 'react';

type CompatibilitySeriesWithSlideDeck =
  | ConversationalUISeriesWithSlideDeck
  | EditorSeriesWithSlideDeck
  | FollowerSeriesWithSlideDeck
  | PowerPointSeriesWithSlideDeck
  | PresentationSeriesWithSlideDeck
  | ViewOnlySeriesWithSlideDeck;

export interface SfinxPresentationSettings {
  seriesId?: CompatibilitySeriesWithSlideDeck['slideDeckPublicKey'];
  qaEnablementScope?: CompatibilitySeriesWithSlideDeck['qaSettings']['enablementScope'];
  liveChatMode?: CompatibilitySeriesWithSlideDeck['liveChatSettings']['liveChatMode'];
  qaModerationPolicy?: CompatibilitySeriesWithSlideDeck['qaSettings']['moderationPolicy'];
  participationIdentityMode?: CompatibilitySeriesWithSlideDeck['participationSettings']['participationIdentityMode'];
  participationIdentityVisibility?: CompatibilitySeriesWithSlideDeck['participationSettings']['participationIdentityVisibility'];
  participationKey?: CompatibilitySeriesWithSlideDeck['participationSettings']['participationKey'];
  participationResponseMode?: CompatibilitySeriesWithSlideDeck['participationSettings']['participationResponseMode'];
  qaQuestionVisibility?: CompatibilitySeriesWithSlideDeck['qaSettings']['questionVisibility'];
  reactionsAllowed?: CompatibilitySeriesWithSlideDeck['reactionSettings']['reactionsAllowed'];
}

interface SfinxPresentationSettingsProviderProps
  extends Omit<SfinxPresentationSettings, 'seriesId'> {
  series: CompatibilitySeriesWithSlideDeck;
}

export interface SfinxPresentationSettingsState {
  series: CompatibilitySeriesWithSlideDeck;
  overrides: SfinxPresentationSettings;
}

export const SfinxPresentationSettingsStateContext = createContext<
  SfinxPresentationSettingsState | undefined
>(undefined);

export const SFINX_PRESENTATION_SETTINGS_OVERRIDE_KEYS: Array<
  keyof SfinxPresentationSettings
> = [
  'seriesId',
  'qaEnablementScope',
  'liveChatMode',
  'qaModerationPolicy',
  'participationIdentityMode',
  'participationIdentityVisibility',
  'participationKey',
  'participationResponseMode',
  'qaQuestionVisibility',
  'reactionsAllowed',
];

export const SfinxPresentationSettingsProvider = ({
  qaEnablementScope: enablementScope,
  liveChatMode,
  qaModerationPolicy: moderationPolicy,
  participationIdentityMode,
  participationIdentityVisibility,
  participationKey,
  participationResponseMode,
  qaQuestionVisibility: questionVisibility,
  reactionsAllowed,
  series,
  children,
}: PropsWithChildren<SfinxPresentationSettingsProviderProps>) => {
  const providerValue = useMemo(() => {
    return {
      series,
      overrides: getOverrides({
        seriesId: series?.slideDeckPublicKey,
        qaEnablementScope: enablementScope,
        liveChatMode,
        qaModerationPolicy: moderationPolicy,
        participationIdentityMode,
        participationIdentityVisibility,
        participationKey,
        participationResponseMode,
        qaQuestionVisibility: questionVisibility,
        reactionsAllowed,
      } as SfinxPresentationSettings),
    };
  }, [
    enablementScope,
    liveChatMode,
    moderationPolicy,
    participationIdentityMode,
    participationIdentityVisibility,
    participationKey,
    participationResponseMode,
    questionVisibility,
    reactionsAllowed,
    series,
  ]);

  return (
    <SfinxPresentationSettingsStateContext.Provider value={providerValue}>
      {children}
    </SfinxPresentationSettingsStateContext.Provider>
  );
};

const maybeAddOverride = <K extends keyof SfinxPresentationSettings>(
  key: K,
  value: SfinxPresentationSettings[K] | undefined,
  overrides: SfinxPresentationSettings,
) => {
  return value === undefined ? overrides : { ...overrides, [key]: value };
};

const getOverrides = (overrides: SfinxPresentationSettings) => {
  return SFINX_PRESENTATION_SETTINGS_OVERRIDE_KEYS.reduce(
    (acc, key) => maybeAddOverride(key, overrides[key], acc),
    {} as SfinxPresentationSettings,
  );
};
